import { TFunction } from 'i18next';
import { JSONSchema7, JSONSchema7TypeName } from 'json-schema';
import convertToYup, { Config } from 'json-schema-yup-transformer';
import { isEmpty, mergeDeepRight } from 'ramda';

import {
  BaseForm,
  CheckFormContent,
  FormContent,
  PricePlanFormContent,
} from 'app/pages/Registration/interfaces';

// eslint-disable-next-line @typescript-eslint/ban-types
const mergeDeep = (first: object, ...rest: object[]) =>
  rest.reduce((acc, cur) => mergeDeepRight(acc, cur), first);

const isContentTypeBoolean = (input: FormContent): input is CheckFormContent =>
  input.type === 'boolean';
const isContentTypePricePlan = (
  input: FormContent,
): input is PricePlanFormContent => input.type === 'pricePlan';

const getSchemaType = (type: FormContent['type']): JSONSchema7['type'] => {
  switch (type) {
    case 'pricePlan':
      return 'object';
    case 'email':
    case 'tel':
    case 'countrySelect':
    case 'password':
      return 'string';
    case 'confirm':
      return 'boolean';
    default:
      return type as JSONSchema7TypeName;
  }
};
export const generateSchema = (
  form: BaseForm,
  t: TFunction = (e: string) => e,
): ReturnType<typeof convertToYup> => {
  const { path } = form;
  const schemaValidation: JSONSchema7 = {};
  if (schemaValidation.properties === undefined) {
    schemaValidation.properties = {
      [path]: { type: 'object', properties: {} },
    };
    schemaValidation.required = [path];
  }
  const schema = schemaValidation.properties[path] as JSONSchema7;
  let config: Config = {
    errors: {},
  };
  if (schema.required === undefined) {
    schema.required = [];
  }
  form.content.forEach((content) => {
    const {
      label: contentLabel,
      type: contentType,
      required: contentRequired,
      ...rest
    } = content;
    if (schema.properties === undefined) {
      schema.properties = {};
    }
    let format;
    if (contentType === 'email') {
      format = 'email';
    }
    let required: string[] | undefined;

    let properties: JSONSchema7['properties'] | undefined;
    const type = getSchemaType(contentType);
    if (isContentTypeBoolean(content) && content.options !== undefined) {
      content.options.forEach((option) => {
        if (schema.properties) {
          schema.properties[option.label] = {
            type: getSchemaType(option.type),
          };
          if (option.required) {
            schema.required?.push(option.label);
          }
        }
      });
      schema.properties[contentLabel] = {
        type,
      };
    } else if (isContentTypePricePlan(content)) {
      properties = {
        period: { type: 'string' },
        plan: { type: 'number' },
      };
      required = ['period', 'plan'];
      schema.properties[contentLabel] = {
        type,
        properties,
        required,
      };
    } else if (content.type === 'password') {
      schema.properties[contentLabel] = {
        type,
        minLength: 8,
        patternProperties: {
          lowerCase: { pattern: '[a-z]' },
          upperCase: { pattern: '[A-Z]' },
          number: { pattern: '[0-9]' },
        },
      };
    } else {
      schema.properties[contentLabel] = {
        type,
        ...(format && { format }),
        ...rest,
      };
    }
    const minNumberValidation = () => {
      if (content.type === 'number' && content.minimum !== undefined) {
        return {
          minimum: `${t('registration.validation.minimum', {
            min: content.minimum,
          })}`,
        };
      }
      return {};
    };
    const requiredValidation = () => {
      if (content.required) {
        return { required: `${t('registration.validation.required')}` };
      }
      return {};
    };
    const emailValidation = () => {
      if (content.type === 'email') {
        return {
          format: { email: `${t('registration.validation.email')}` },
        };
      }
      return {};
    };
    const passwordValidation = () => {
      if (content.type === 'password') {
        return {
          minLength: t('registration.validation.passwordLength'),
          lowerCase: t('registration.validation.lowerCase'),
          upperCase: t('registration.validation.upperCase'),
          number: t('registration.validation.number'),
        };
      }
      return {};
    };
    const mergeResult = mergeDeep(
      requiredValidation(),
      emailValidation(),
      minNumberValidation(),
      passwordValidation(),
    );
    const elementConfig: Config = {
      errors: {
        [path]: {
          [content.label]: {
            ...mergeResult,
          },
        },
      },
    };

    if (contentRequired) {
      schema.required?.push(content.label);
    }
    if (!isEmpty(mergeResult)) {
      config = mergeDeep(config, elementConfig);
    }
  });
  return convertToYup(schemaValidation, config);
};
